/**
 * Process Status Utility
 * 
 * Calculates process compliance status for job applications
 * Minimal design - just tracks required steps for interview readiness
 */

/**
 * Calculate process compliance status for an application
 * @param {Object} application - Job application object
 * @returns {Object} Process status with score, status, and message
 */
export function getProcessStatus(application) {
  if (!application) {
    return { status: 'pending', score: 0, message: 'Application not found' };
  }

  const appStatus = application.status || 'new';

  // Blocked state - cannot proceed
  if (['rejected', 'withdrawn'].includes(appStatus)) {
    return {
      status: 'blocked',
      score: 0,
      message: 'Cannot schedule interview for rejected or withdrawn candidate'
    };
  }

  // Check required steps
  const reviewed = appStatus !== 'new';
  const shortlisted = [
    'shortlisted',
    'screening_call',
    'screening_passed',
    'interview',
    'assessment_sent',
    'assessment_completed',
    'offer',
    'accepted',
    'declined',
    'hired'
  ].includes(appStatus);

  // Calculate score (50 points per required step)
  const score = (reviewed ? 50 : 0) + (shortlisted ? 50 : 0);

  if (score === 100) {
    return {
      status: 'ready',
      score: 100,
      message: 'Ready'
    };
  } else if (score === 50) {
    if (!reviewed) {
      return {
        status: 'pending',
        score: 50,
        message: 'Review required'
      };
    } else {
      return {
        status: 'pending',
        score: 50,
        message: 'Consider shortlisting first'
      };
    }
  } else {
    return {
      status: 'pending',
      score: 0,
      message: 'Review required'
    };
  }
}

/**
 * Get process status badge class for styling
 * @param {string} status - Process status ('ready', 'pending', 'blocked')
 * @returns {string} CSS class name
 */
export function getProcessStatusClass(status) {
  const baseClass = 'process-status';
  switch (status) {
    case 'ready':
      return `${baseClass} process-status-ready`;
    case 'pending':
      return `${baseClass} process-status-pending`;
    case 'blocked':
      return `${baseClass} process-status-blocked`;
    default:
      return baseClass;
  }
}

/**
 * Get process status dot class for styling
 * @param {string} status - Process status ('ready', 'pending', 'blocked')
 * @returns {string} CSS class name
 */
export function getProcessDotClass(status) {
  const baseClass = 'process-dot';
  switch (status) {
    case 'ready':
      return `${baseClass} process-dot-ready`;
    case 'pending':
      return `${baseClass} process-dot-pending`;
    case 'blocked':
      return `${baseClass} process-dot-blocked`;
    default:
      return baseClass;
  }
}


